import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const packages = [
  { value: 'foundation', label: 'Foundation $297/month', desc: 'Build discipline and structure' },
  { value: 'ascend', label: 'Ascend $597/month', desc: 'Physical and mental elevation' },
  { value: 'legacy-elite', label: 'Legacy Elite $1,500/month', desc: 'High-performance transformation' },
];

const goals = [
  'Build Muscle & Strength',
  'Lose Body Fat',
  'Improve Nutrition',
  'Mindset & Discipline',
  'Executive Performance',
  'Overall Lifestyle Upgrade',
];

export default function BookingForm() {
  const headerRef = useScrollAnimation();
  const formRef = useScrollAnimation({ delay: 100 });
  
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [charCount, setCharCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const toggleGoal = (goal: string) => {
    setSelectedGoals(prev =>
      prev.includes(goal) ? prev.filter(g => g !== goal) : [...prev, goal]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    if (selectedGoals.length === 0) {
      setError('Please select at least one goal.');
      return;
    }

    const data = new URLSearchParams();
    const fields = ['fullName', 'email', 'phone', 'package', 'message'];
    fields.forEach(name => {
      const el = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el) data.append(name, el.value);
    });
    data.append('goals', selectedGoals.join(', '));

    setSubmitting(true);
    try {
      const res = await fetch('https://readdy.ai/api/form/d6kppoc6fojmejgaiobg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" style={{ background: '#0d0d0d' }} className="py-20 sm:py-24 px-4">
      <div className="max-w-3xl mx-auto">
        <div ref={headerRef} className="animate-fade-up text-center mb-10 sm:mb-14">
          <p className="text-xs font-bold tracking-[0.35em] uppercase mb-3" style={{ color: '#B8860B' }}>
            Ready to Begin
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
            Book Your Free<br />
            <span style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Consultation Call
            </span>
          </h2>
          <p className="text-sm leading-relaxed max-w-xl mx-auto" style={{ color: '#888888' }}>
            Fill out the form below and Benny will personally reach out to discuss your goals and find the right package for you.
          </p>
        </div>

        {submitted ? (
          <div ref={formRef} className="animate-scale-up rounded-2xl p-10 sm:p-12 text-center" style={{ background: '#1a1a1a', border: '1px solid #B8860B' }}>
            <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-6"
              style={{ background: 'linear-gradient(135deg, #B8860B, #F5D76E)' }}>
              <i className="ri-check-line text-3xl text-black"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Application Received!</h3>
            <p className="text-sm" style={{ color: '#888888' }}>
              Thank you for reaching out. Benny will contact you within 24 to 48 hours to schedule your consultation call.
            </p>
          </div>
        ) : (
          <form
            ref={formRef}
            data-readdy-form
            onSubmit={handleSubmit}
            className="animate-scale-up rounded-2xl p-6 sm:p-10 space-y-6"
            style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#B8860B' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  required
                  placeholder="John Smith"
                  className="w-full rounded-md px-4 py-3 text-sm text-white outline-none transition-all duration-200"
                  style={{ background: '#111111', border: '1px solid #2a2a2a', color: '#ffffff' }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#B8860B')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#2a2a2a')}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#B8860B' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="john@example.com"
                  className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all duration-200"
                  style={{ background: '#111111', border: '1px solid #2a2a2a', color: '#ffffff' }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#B8860B')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#2a2a2a')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#B8860B' }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="+1 (555) 000 0000"
                  className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all duration-200"
                  style={{ background: '#111111', border: '1px solid #2a2a2a', color: '#ffffff' }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#B8860B')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#2a2a2a')}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#B8860B' }}>
                  Interested Package *
                </label>
                <select
                  name="package"
                  required
                  className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all duration-200 cursor-pointer"
                  style={{ background: '#111111', border: '1px solid #2a2a2a', color: '#ffffff' }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#B8860B')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#2a2a2a')}
                >
                  <option value="" style={{ background: '#111111' }}>Select a package...</option>
                  {packages.map(p => (
                    <option key={p.value} value={p.value} style={{ background: '#111111' }}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#B8860B' }}>
                Primary Goals * <span className="normal-case tracking-normal font-normal text-xs" style={{ color: '#555' }}>(select all that apply)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {goals.map(goal => {
                  const active = selectedGoals.includes(goal);
                  return (
                    <button
                      key={goal}
                      type="button"
                      onClick={() => toggleGoal(goal)}
                      className="px-3 py-2 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                      style={{
                        background: active ? 'linear-gradient(90deg, #B8860B, #F5D76E)' : '#111111',
                        color: active ? '#000000' : '#888888',
                        border: active ? '1px solid transparent' : '1px solid #2a2a2a',
                      }}
                    >
                      {active && <i className="ri-check-line mr-1"></i>}
                      {goal}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#B8860B' }}>
                Tell Us About Yourself
              </label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                placeholder="Share your current situation, biggest challenges, and what you want to achieve..."
                className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all duration-200 resize-none"
                style={{ background: '#111111', border: '1px solid #2a2a2a', color: '#ffffff' }}
                onFocus={e => (e.currentTarget.style.borderColor = '#B8860B')}
                onBlur={e => (e.currentTarget.style.borderColor = '#2a2a2a')}
                onChange={e => setCharCount(e.target.value.length)}
              />
              <p className="text-right text-xs mt-1" style={{ color: charCount > 450 ? '#C8102E' : '#555555' }}>
                {charCount}/500
              </p>
            </div>

            {error && (
              <p className="text-sm text-center py-2 rounded-md" style={{ color: '#C8102E', background: 'rgba(200,16,46,0.08)', border: '1px solid rgba(200,16,46,0.2)' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-md font-bold text-base tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{
                background: submitting ? '#555' : 'linear-gradient(90deg, #B8860B, #F5D76E)',
                color: '#000000',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-calendar-check-line"></i> Book My Free Consultation
                </span>
              )}
            </button>

            <p className="text-center text-xs" style={{ color: '#444444' }}>
              No commitment required. Benny will personally reach out within 24 to 48 hours.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}